/**
 * Normalization utilities for OpenSCAD source code.
 *
 * These functions are used for CHANGE DETECTION ONLY — to determine whether
 * a code edit affects geometry. The normalized form is never sent to the WASM
 * compiler or used for error reporting. The original source is always preserved
 * for those purposes so that error line numbers remain accurate.
 */

/**
 * Strip all OpenSCAD comments from a source string.
 *
 * Handles:
 * - Line comments: // …  (up to end of line; newline is preserved)
 * - Block comments: /* … *\/  (including multi-line; replaced with whitespace
 *   so that token positions within a line are not shifted for human reading,
 *   though exact column positions are not preserved — that's fine since this
 *   output is only used for hashing)
 *
 * String literals are respected: comment markers inside strings are not treated
 * as comments.
 */
export function stripScadComments(code: string): string {
  const out: string[] = [];
  let i = 0;
  const n = code.length;

  while (i < n) {
    const ch = code[i];

    // String literal — pass through verbatim (don't interpret // or /* inside)
    if (ch === '"') {
      out.push(ch);
      i++;
      while (i < n) {
        const sc = code[i];
        out.push(sc);
        if (sc === '\\') {
          // escape sequence — consume next char too
          i++;
          if (i < n) {
            out.push(code[i]);
          }
        } else if (sc === '"') {
          break;
        }
        i++;
      }
      i++;
      continue;
    }

    // Check for comment start
    if (ch === '/' && i + 1 < n) {
      const next = code[i + 1];

      // Line comment: // …
      if (next === '/') {
        // Skip everything up to (but not including) the newline so the line
        // count is preserved.
        i += 2;
        while (i < n && code[i] !== '\n') {
          i++;
        }
        // Leave the newline to be processed in the next iteration.
        continue;
      }

      // Block comment: /* … */
      if (next === '*') {
        i += 2;
        while (i < n) {
          if (code[i] === '*' && i + 1 < n && code[i + 1] === '/') {
            i += 2;
            break;
          }
          // Preserve newlines so line counts stay correct
          if (code[i] === '\n') out.push('\n');
          i++;
        }
        continue;
      }
    }

    out.push(ch);
    i++;
  }

  return out.join('');
}

/**
 * Collapse runs of 3 or more consecutive blank lines into 2 blank lines.
 * Trailing whitespace on lines is also trimmed.
 */
function collapseBlankLines(code: string): string {
  return code.replace(/(\n[ \t]*){3,}/g, '\n\n');
}

/**
 * Produce a canonical form of the code suitable for change-detection hashing.
 *
 * What this does:
 * 1. Strip all comments (line and block)
 * 2. Collapse excess blank lines
 *
 * What this does NOT do:
 * - Normalize whitespace within lines (indentation, spacing around operators)
 * - Change any token, identifier, or value
 *
 * IMPORTANT: The returned string is only used for hashing / equality checks.
 * Never pass it to the WASM compiler or use it for error reporting.
 */
export function normalizeScadCode(code: string): string {
  return collapseBlankLines(stripScadComments(code));
}

/**
 * Fast non-cryptographic string hash (FNV-1a 32-bit).
 * Suitable for change-detection; not for security.
 */
export function hashScadCode(code: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < code.length; i++) {
    h ^= code.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in 32-bit unsigned range
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Returns a hash of the code with ALL Meshpit scene-extension module calls
 * removed, in addition to comments. This is the "geometry-only" hash: it
 * changes only when the actual geometry/color/parameter logic changes, not
 * when lights, PiP cameras, or scene settings are edited.
 *
 * The modules stripped here match MESHPIT_STANDALONE_MODULES in
 * scad-preprocessor.ts plus camera_pip and camera_path (already in that list).
 */
export function geometryHash(normalizedCode: string): number {
  const stripped = stripSceneExtensionCalls(normalizedCode);
  return hashScadCode(stripped);
}

/**
 * Remove all standalone Meshpit scene-extension module calls from the code.
 * Only the call site is removed; no children are affected (these modules
 * never have children that contribute geometry).
 *
 * Modules removed:
 *   light_ambient, light_directional, light_point, light_spot,
 *   scene_material, scene_default_lights, scene_shadows, scene_bloom,
 *   scene_ssao, scene_background, scene_grid, camera_pip, camera_path
 */
const SCENE_EXTENSION_MODULES = [
  'light_ambient',
  'light_directional',
  'light_point',
  'light_spot',
  'scene_material',
  'scene_default_lights',
  'scene_shadows',
  'scene_bloom',
  'scene_ssao',
  'scene_background',
  'scene_grid',
  'scene_camera',
  'camera_pip',
  'camera_path',
] as const;

function stripSceneExtensionCalls(code: string): string {
  // Build one regex that matches any of the module names as whole words.
  // We match: optional leading whitespace, module name, balanced parens, optional ;
  // This is a simplified stripper — good enough for hashing since we just
  // need the geometry-relevant content to be identical across scene-only edits.
  const namePattern = SCENE_EXTENSION_MODULES.join('|');
  // Replace each call (name + balanced paren block + optional semicolon) with empty string.
  // We use a simple approach: find the name, then find the matching closing paren.
  let result = code;
  let changed = true;
  // Iterate until stable (handles edge cases like nested or adjacent calls)
  while (changed) {
    changed = false;
    const re = new RegExp(`(?<![a-zA-Z_$0-9])(${namePattern})\\s*\\(`, 'g');
    let m: RegExpExecArray | null;
    // Collect all match positions first (right-to-left replacement preserves indices)
    const matches: Array<{ start: number; parenOpen: number }> = [];
    while ((m = re.exec(result)) !== null) {
      matches.push({ start: m.index, parenOpen: m.index + m[0].length - 1 });
    }
    if (matches.length === 0) break;

    // Process from last to first so splice positions remain valid
    for (let k = matches.length - 1; k >= 0; k--) {
      const { start, parenOpen } = matches[k];
      const closeIdx = findBalancedParen(result, parenOpen);
      if (closeIdx === -1) continue;

      let end = closeIdx;
      // Consume optional trailing semicolon
      while (
        end < result.length &&
        (result[end] === ' ' || result[end] === '\t')
      )
        end++;
      if (end < result.length && result[end] === ';') end++;

      result = result.slice(0, start) + result.slice(end);
      changed = true;
    }
  }
  return result;
}

/**
 * Find the index after the matching closing ')' for an opening '(' at `openIdx`.
 * Returns -1 if not found. Respects string literals.
 */
function findBalancedParen(code: string, openIdx: number): number {
  let depth = 0;
  let inStr = false;
  for (let i = openIdx; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"' && (i === 0 || code[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}
